import { useNavigationLinks } from 'utils/hooks/useNavigationLinks';
import { Link, NavLink, useLocation } from 'react-router';
import { IconX } from '@tabler/icons-react';
import { Button, createTheme } from 'flowbite-react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import AboutThisPageLink from 'components/HelpDropdown/AboutThisPageLink';
import { IconExternalLink } from '@tabler/icons-react';
import { Brand } from 'components/Brand';

const buttonTheme = createTheme({
    button: {
        base: 'border-none !bg-transparent absolute top-2.5 right-2.5 !text-primary xxl:hidden',
        size: {
            md: '!p-0',
        },
    },
}).button;

interface Props {
    setIsSidebarOpen: (open: boolean) => void;
}

export const MobileNavigation = ({ setIsSidebarOpen }: Props) => {
    const { t } = useTranslation();
    const { navigationLinks, helpLinks } = useNavigationLinks();
    const { pathname } = useLocation();

    return (
        <>
            <Button
                theme={buttonTheme}
                onClick={() => setIsSidebarOpen(false)}
                aria-label={t(`layout:navigation-menu:collapse`)}
            >
                <IconX size={16} />
            </Button>
            <div className="px-4 py-6 w-full h-full xxl:hidden">
                <Brand className="text-primary-700" />

                <div className="flex flex-col mt-4 gap-3">
                    {navigationLinks.map(
                        ({
                            id,
                            href,
                            text,
                            icon: Icon,
                            isActiveLink,
                            reloadOnActive,
                        }) => (
                            <NavLink
                                key={id}
                                to={href}
                                reloadDocument={
                                    reloadOnActive && pathname === href
                                }
                                className="flex gap-2 items-center group"
                            >
                                {({ isActive }) => (
                                    <>
                                        <Icon
                                            size={16}
                                            className={classNames({
                                                'text-primary group-hover:text-primary-700 group/selected': true,
                                                'text-primary-700':
                                                    isActive ||
                                                    isActiveLink?.(pathname),
                                            })}
                                        />
                                        <span
                                            className={classNames({
                                                'font-medium text-base leading-6 text-primary group-hover:text-primary-700': true,
                                                'text-primary-700':
                                                    isActive ||
                                                    isActiveLink?.(pathname),
                                            })}
                                        >
                                            {text}
                                        </span>
                                    </>
                                )}
                            </NavLink>
                        )
                    )}
                </div>

                <div className="h-px my-4 bg-gray-200 w-full"></div>

                <div className="flex flex-col mt-4 gap-3">
                    <AboutThisPageLink />
                    {helpLinks.map((link) => (
                        <Link
                            onClick={() => setIsSidebarOpen(false)}
                            key={link.id}
                            to={link.href}
                            target="_blank"
                            className="flex gap-2 items-center group"
                        >
                            <span className="flex items-center gap-2 font-medium text-base leading-6 text-primary group-hover:text-primary-700">
                                {link.text}
                                <IconExternalLink size={20} />
                            </span>
                        </Link>
                    ))}
                </div>
            </div>
        </>
    );
};
